/**
 * Requested-chat lifecycle rules (issue #123).
 *
 * A `requested` chat is a buyer-initiated request that an operator fulfils by
 * assigning a distributor and then explicitly activating it with
 * `chatUpdate({ status: 'active' })`. These pure helpers encode the
 * status-based gating that the legacy admin enforced (see
 * `legacy-repos/choco-admin-chats/.../EditChatDetailsFormTabPanels/Form.tsx`):
 *
 *   - supplier/distributor select is editable ONLY when status === 'requested'
 *   - buyer select is editable for every status EXCEPT 'pending'
 *   - the "Activate chat" action exists only for 'requested' chats and stays
 *     disabled until a distributor entity is present
 *
 * `enabled` is an independent flag (the Settings-tab toggle) and is
 * deliberately NOT consulted here — activation is a status transition, not an
 * enable/disable.
 */
import type { ChatDetailsResponse, ChatStatus } from '@choco-admin/types';
import { CreatedContext } from '@choco-admin/types';

/** The chat is a buyer-initiated request awaiting fulfilment. */
export const isRequestedChat = (status: ChatStatus): boolean =>
  status === 'requested';

/**
 * A distributor may be assigned/changed only while the chat is `requested`.
 * Mirrors legacy `withEditableSupplierSelect={chatStatus === Requested}` — in
 * particular an `active` chat can no longer change its distributor here.
 */
export const canAssignDistributor = (status: ChatStatus): boolean =>
  status === 'requested';

/**
 * The buyer may be changed for every status except `pending`.
 * Mirrors legacy `withEditableBuyerSelect={chatStatus !== Pending}`.
 */
export const canEditBuyer = (status: ChatStatus): boolean =>
  status !== 'pending';

/** True once a distributor entity is linked to the chat. */
export const hasAssignedDistributor = (
  chatData: Pick<ChatDetailsResponse, 'supplierEntity' | 'supplierEntityId'>
): boolean => Boolean(chatData.supplierEntityId || chatData.supplierEntity);

/**
 * Activation is available only for a `requested` chat that already has the
 * required entity (a distributor). Mirrors the legacy "Activate chat" button
 * being `disabled={!hasSupplierEntity}`.
 */
export const canActivateChat = (
  chatData: Pick<
    ChatDetailsResponse,
    'status' | 'supplierEntity' | 'supplierEntityId'
  >
): boolean =>
  isRequestedChat(chatData.status) && hasAssignedDistributor(chatData);

export interface FulfilmentStep {
  id: string;
  label: string;
  done: boolean;
}

export interface FulfilmentGuidance {
  intro: string;
  steps: FulfilmentStep[];
}

/**
 * Request-fulfilment guidance derived from `createdContext`, mirroring the
 * legacy `Wizard`:
 *
 *   - buyerAddsVerifiedSupplierNewCustomer → the distributor is already on the
 *     chat, so the only step is activation.
 *   - buyerRequests…{Existing,New}Customer → the operator must first assign a
 *     distributor entity, then activate.
 *
 * Returns `null` for any other/unknown context (no specific guidance to show).
 * `done` flags track live progress so the panel can render ✅/❌ per step.
 */
export const getRequestFulfilmentGuidance = (
  createdContext: CreatedContext | null | undefined,
  state: { hasDistributor: boolean; isActivated: boolean }
): FulfilmentGuidance | null => {
  const activateStep: FulfilmentStep = {
    id: 'activate',
    label: 'Click "Activate chat" to fulfil the request',
    done: state.isActivated,
  };

  switch (createdContext) {
    case CreatedContext.BUYER_ADDS_VERIFIED_SUPPLIER_NEW_CUSTOMER:
      return {
        intro:
          'A user created this chat by adding a Verified Supplier they are not yet a customer of. To activate this chat, you need to:',
        steps: [activateStep],
      };
    case CreatedContext.BUYER_REQUESTS_SUPPLIER_EXISTING_CUSTOMER:
    case CreatedContext.BUYER_REQUESTS_SUPPLIER_NEW_CUSTOMER:
      return {
        intro:
          'A user created this chat via the "Add Any Supplier" in-app form. To activate this chat, you need to:',
        steps: [
          {
            id: 'distributor',
            label:
              'Assign a distributor entity to the chat (the details are in the Salesforce ticket)',
            done: state.hasDistributor,
          },
          activateStep,
        ],
      };
    default:
      return null;
  }
};
